import React from 'react'
import { Modal, Table } from 'react-bootstrap'

interface OzonData {
    OzonId?: number
    OzonOfferId?: number
    OzonSKU?: number
    OzonPrice?: string
    OzonStocks?: {
        stocks?: Array<{
            sku: number
            source: string
            present: number
            reserved: number
        }>
        has_stock?: boolean
    }
    OzonHasPrice?: boolean
    OzonHasStock?: boolean
    OzonStatuses?: {
        status?: string
        is_created?: boolean
        status_name?: string
        status_failed?: string
        status_tooltip?: string
        moderate_status?: string
        status_updated_at?: string
        validation_status?: string
        status_description?: string
    }
}

interface IProps {
    isOpen: boolean
    onClose: () => void
    ozonData: OzonData
}

const ModalOzonDetails = ({ isOpen, onClose, ozonData }: IProps) => {
    const formatPrice = (price: string | undefined) => {
        if (!price) return '-'
        return parseFloat(price).toLocaleString('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
    }

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '-'
        try {
            return new Date(dateString).toLocaleString('ru-RU')
        } catch {
            return dateString
        }
    }

    const formatSKULink = (sku: number | undefined) => {
        if (!sku) return '-'
        return (
            <a 
                href={`https://ozon.ru/product/${sku}`} 
                target="_blank" 
                rel="noopener noreferrer"
            >
                {sku}
            </a>
        )
    }

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Данные Ozon</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Table striped bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Параметр</th>
                            <th>Значение</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Ozon ID</strong></td>
                            <td>{ozonData.OzonId || '-'}</td>
                        </tr>
                        <tr>
                            <td><strong>Ozon SKU</strong></td>
                            <td>{formatSKULink(ozonData.OzonSKU)}</td>
                        </tr>
                        <tr>
                            <td><strong>Ozon OfferID</strong></td>
                            <td>{ozonData.OzonOfferId || '-'}</td>
                        </tr>
                        <tr>
                            <td><strong>Цена</strong></td>
                            <td>{formatPrice(ozonData.OzonPrice)} ₽</td>
                        </tr>
                        <tr>
                            <td><strong>Есть цена</strong></td>
                            <td>{ozonData.OzonHasPrice ? 'Да' : 'Нет'}</td>
                        </tr>
                        <tr>
                            <td><strong>Есть остатки</strong></td>
                            <td>{ozonData.OzonHasStock ? 'Да' : 'Нет'}</td>
                        </tr>
                        
                        {ozonData.OzonStocks && (
                            <>
                                <tr>
                                    <td><strong>Остатки</strong></td>
                                    <td>{ozonData.OzonStocks.has_stock ? 'Есть' : 'Нет'}</td>
                                </tr>
                                {ozonData.OzonStocks.stocks && ozonData.OzonStocks.stocks.length > 0 && (
                                    <tr>
                                        <td><strong>Детали остатков</strong></td>
                                        <td>
                                            <Table size="sm" className="mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>SKU</th>
                                                        <th>Источник</th>
                                                        <th>В наличии</th>
                                                        <th>Зарезервировано</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ozonData.OzonStocks.stocks.map((stock, index) => (
                                                        <tr key={index}>
                                                            <td>{stock.sku}</td>
                                                            <td>{stock.source}</td>
                                                            <td>{stock.present}</td>
                                                            <td>{stock.reserved}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}

                        {ozonData.OzonStatuses && (
                            <>
                                <tr>
                                    <td><strong>Статус</strong></td>
                                    <td>{ozonData.OzonStatuses.status_name || ozonData.OzonStatuses.status || '-'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Создан</strong></td>
                                    <td>{ozonData.OzonStatuses.is_created ? 'Да' : 'Нет'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Статус модерации</strong></td>
                                    <td>{ozonData.OzonStatuses.moderate_status || '-'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Статус валидации</strong></td>
                                    <td>{ozonData.OzonStatuses.validation_status || '-'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Обновлено</strong></td>
                                    <td>{formatDate(ozonData.OzonStatuses.status_updated_at)}</td>
                                </tr>
                                {ozonData.OzonStatuses.status_description && (
                                    <tr>
                                        <td><strong>Описание статуса</strong></td>
                                        <td>{ozonData.OzonStatuses.status_description}</td>
                                    </tr>
                                )}
                                {ozonData.OzonStatuses.status_tooltip && (
                                    <tr>
                                        <td><strong>Подсказка</strong></td>
                                        <td>{ozonData.OzonStatuses.status_tooltip}</td>
                                    </tr>
                                )}
                                {ozonData.OzonStatuses.status_failed && (
                                    <tr>
                                        <td><strong>Ошибка</strong></td>
                                        <td style={{ color: 'red' }}>{ozonData.OzonStatuses.status_failed}</td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <button 
                    className="btn btn-secondary" 
                    onClick={onClose}
                >
                    Закрыть
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default ModalOzonDetails
